import { breakpoints } from '@constants'
import ArrowDownIcon from '@images/icons/arrow-down-icon.svg?react'
import { formatCurrencyForDisplay } from '@utils'
import cn from 'classnames'
import { CurrencyTypes, useCurrencies, useGetAggregatedBalanceQuery } from 'mmfintech-backend-api'
import { extractCurrencyCode, formatFloat, isValidArray, tr, useMatchMedia } from 'mmfintech-commons'
import moment from 'moment'
import { useState } from 'react'
import { Dot, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { CoreTabs } from '../CoreTabs'
import { PaymentActionButton } from '../PaymentActionButtons'

import './CoreGraphSection.scss'

const tabs = [
  {
    get label() {
      return tr('FRONTEND.EXPENSES.FILTER.ALL', 'All')
    },
    value: 'All'
  },
  {
    get label() {
      return tr('FRONTEND.HEADER.CRYPTO', 'Crypto')
    },
    value: 'Crypto'
  },
  {
    get label() {
      return tr('FRONTEND.ACCOUNTS.FIAT_ACCOUNTS_LABEL', 'Fiat')
    },
    value: 'Fiat'
  }
]

export const CoreGraphSection = () => {
  const [currentTab, setCurrentTab] = useState<string>('All')
  const isMobile = useMatchMedia({
    breakpoint: breakpoints.MAX_MOBILE_WIDTH,
    prefix: 'max'
  })
  const isSmallScreen = useMatchMedia({ breakpoint: 436, prefix: 'max' })

  const { getCurrencyPrecision } = useCurrencies()

  const { aggregatedBalance } = useGetAggregatedBalanceQuery(
    {
      dateFrom: moment().subtract(6, 'days').format('YYYY-MM-DD'),
      dateTo: moment().format('YYYY-MM-DD'),
      currencyType:
        currentTab === 'All' ? undefined : currentTab === 'Crypto' ? CurrencyTypes.CRYPTO : CurrencyTypes.FIAT
    },
    {
      refetchOnMountOrArgChange: true,
      selectFromResult: ({ data }) => ({
        aggregatedBalance: isValidArray(data)
          ? data.map(el => {
              return {
                date: moment(el.date).format('D'),
                amount: el.amount,
                currencyCode: el.currencyCode
              }
            })
          : []
      })
    }
  )

  const minValue = () => {
    if (isValidArray(aggregatedBalance)) {
      const minVal = Math.min(...aggregatedBalance.map(balance => balance.amount))
      const average =
        [...aggregatedBalance.map(balance => balance.amount)].reduce((p, c) => p + c, 0) / aggregatedBalance.length
      const percentage = (average - minVal) / average
      return minVal * (1 - percentage / 3)
    }
  }

  const maxValue = () => {
    if (isValidArray(aggregatedBalance)) {
      return Math.max(...aggregatedBalance.map(balance => balance.amount))
    }
  }

  return (
    <div className='graph-container'>
      <div className='graph-header'>
        {!isSmallScreen && <h6>{tr('FRONTEND.DASHBOARD.TITLE', 'Overview')}</h6>}
        <CoreTabs tabs={tabs} onChangeTab={setCurrentTab} tabState={currentTab} />
        <div className='graph-divider'></div>
      </div>
      <div className='graph-content-container'>
        <div className='graph-left-side'>
          <div className='graph-left-side-header'>
            <div className='graph-title'>
              {currentTab === 'All'
                ? tr('FRONTEND.GRAPH.ALL_BALANCES', 'All balances')
                : currentTab === 'Crypto'
                  ? tr('FRONTEND.GRAPH.CRYPTO_BALANCE', 'Crypto balance')
                  : tr('FRONTEND.GRAPH.FIAT_BALANCE', 'Fiat balance')}
            </div>
            <span className='graph-amount'>
              {isValidArray(aggregatedBalance) &&
                formatFloat(
                  aggregatedBalance[aggregatedBalance.length - 1].amount,
                  getCurrencyPrecision(extractCurrencyCode(aggregatedBalance[aggregatedBalance.length - 1]))
                )}
              {isValidArray(aggregatedBalance) && (
                <span className='graph-amount-currency'>
                  {extractCurrencyCode(aggregatedBalance[aggregatedBalance.length - 1])}
                </span>
              )}
            </span>
          </div>
          <div className='payment-action-button-group'>
            <PaymentActionButton type='deposit' />
            <PaymentActionButton type='send' />
            <PaymentActionButton type='exchange' />
          </div>
        </div>
        {!isMobile && (
          <div className='graph-right-side'>
            <ResponsiveContainer width='100%' height={211}>
              <LineChart data={aggregatedBalance}>
                <Tooltip content={<CustomTooltip balanceArray={aggregatedBalance} />} cursor={<CustomCursor />} />
                <YAxis hide={true} domain={[minValue(), maxValue()]} />
                <XAxis
                  dataKey='date'
                  allowDecimals={false}
                  axisLine={false}
                  tickLine={false}
                  fontSize={14}
                  fontWeight={700}
                  padding={{ left: 10, right: 10 }}
                />
                <Dot r={1} />
                <Line
                  isAnimationActive={false}
                  type='linear'
                  dataKey='amount'
                  stroke='red'
                  strokeWidth={2.5}
                  strokeLinejoin='miter'
                  strokeLinecap='square'
                  dot={null}
                  activeDot={{
                    r: 3,
                    fill: '#fff',
                    stroke: 'red',
                    strokeWidth: 2
                  }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  )
}

const CustomTooltip = ({ balanceArray, ...external }) => {
  const { amount, currencyCode, date } = external?.payload?.[0]?.payload || {}
  const selectedElement = balanceArray.find(el => el.date === date)
  const selectedIndex = balanceArray.indexOf(selectedElement)

  const isPositive = () => {
    if (selectedIndex === 0) return true
    if (selectedIndex >= 0) {
      return amount > balanceArray[selectedIndex - 1].amount
    }
    return false
  }

  return (
    <div className='custom-tooltip'>
      <div
        className={cn('icon', {
          positive: isPositive(),
          negative: !isPositive()
        })}
      >
        <ArrowDownIcon />
      </div>
      <div>
        {amount} <span className='custom-tooltip-currency'>{formatCurrencyForDisplay(currencyCode)}</span>
      </div>
    </div>
  )
}

const CustomCursor = props => {
  const dot = document.getElementsByClassName('recharts-active-dot')[0]

  if (dot) {
    const activeDot = dot.childNodes[0] as HTMLElement
    const activeDotPos = activeDot?.getAttribute('cy')
    const { points } = props
    const startingPoint = points[0]
    const endingPoint = points[1]
    const [x1, y1] = [startingPoint.x, Number(activeDotPos)]
    const [x2, y2] = [endingPoint.x, endingPoint.y]
    if (activeDot?.getAttribute('cx') !== endingPoint.x) {
      return null
    }
    return (
      <>
        <svg x1={x1} y1={y1} x2={x2} y2={y2}>
          <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={'rgba(255, 0, 0, 1)'} strokeWidth={1} />
        </svg>
        <circle cx={x1} cy='205' r='3' fill='red'></circle>
      </>
    )
  }
  return null
}
